import React, { useEffect, useMemo, useState } from "react";

/**
 * PT‑BR Past Tense Trainer
 * Focus: pretérito perfeito & imperfeito (contrast-friendly)
 * Single-file React component. Tailwind-friendly, no external deps.
 */

interface Card {
  prompt_en: string;
  target_pt: string;
  tense: "perfeito" | "imperfeito" | "contrast";
  verb: string;
  pronoun: string;
  explain_pt: string;
  explain_en: string;
  hint_pt: string;
  triggers: string[];
}

interface IconProps {
  className?: string;
  [key: string]: any;
}

// ——— Inline Icons ———
const IconWand = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M15 4l5 5M3 21l9-9"/>
    <path d="M8 3h2M3 8h2M13 8h2M8 13h2" strokeLinecap="round"/>
  </svg>
);
const IconCopy = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <rect x="9" y="9" width="13" height="13" rx="2"/>
    <rect x="2" y="2" width="13" height="13" rx="2"/>
  </svg>
);
const IconPlay = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <polygon points="6,4 20,12 6,20" fill="currentColor"/>
  </svg>
);
const IconCheck = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconEye = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconDownload = (props: IconProps) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <path d="M12 3v12"/>
    <path d="M7 10l5 5 5-5"/>
    <rect x="3" y="18" width="18" height="3" rx="1.5"/>
  </svg>
);

// ——— Small helpers ———
const stripDiacritics = (s: string) => s
  .normalize("NFD")
  .replace(/\p{Diacritic}/gu, "")
  .replace(/[^\p{L}\p{N}\s\-']/gu, "");

const normalize = (s: string) => stripDiacritics(String(s || "").toLowerCase().trim())
  .replace(/\s+/g, " ");

// Levenshtein distance (simple)
function lev(a: string, b: string) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }
  return dp[m][n];
}

function isCloseMatch(user: string, target: string) {
  const u = normalize(user);
  const t = normalize(target);
  if (!u) return false;
  if (u === t) return true;
  const d = lev(u, t);
  const tol = Math.max(1, Math.floor(t.length * 0.15));
  return d <= tol;
}

// ——— Minimal conjugator (regular verbs + key irregulars) ———
const IRREG: Record<string, Record<string, Record<string, string>>> = {
  // pretérito perfeito
  ir: { perfeito: { eu: "fui", voce: "foi", ele: "foi", ela: "foi", a_gente: "foi", nos: "fomos", voces: "foram", eles: "foram", elas: "foram" },
        imperfeito: { eu: "ia", voce: "ia", ele: "ia", ela: "ia", a_gente: "ia", nos: "íamos", voces: "iam", eles: "iam", elas: "iam" } },
  ser: { perfeito: { eu: "fui", voce: "foi", ele: "foi", ela: "foi", a_gente: "foi", nos: "fomos", voces: "foram", eles: "foram", elas: "foram" },
         imperfeito: { eu: "era", voce: "era", ele: "era", ela: "era", a_gente: "era", nos: "éramos", voces: "eram", eles: "eram", elas: "eram" } },
  ter: { perfeito: { eu: "tive", voce: "teve", ele: "teve", ela: "teve", a_gente: "teve", nos: "tivemos", voces: "tiveram", eles: "tiveram", elas: "tiveram" },
         imperfeito: { eu: "tinha", voce: "tinha", ele: "tinha", ela: "tinha", a_gente: "tinha", nos: "tínhamos", voces: "tinham", eles: "tinham", elas: "tinham" } },
  estar: { perfeito: { eu: "estive", voce: "esteve", ele: "esteve", ela: "esteve", a_gente: "esteve", nos: "estivemos", voces: "estiveram", eles: "estiveram", elas: "estiveram" },
           imperfeito: { eu: "estava", voce: "estava", ele: "estava", ela: "estava", a_gente: "estava", nos: "estávamos", voces: "estavam", eles: "estavam", elas: "estavam" } },
  fazer: { perfeito: { eu: "fiz", voce: "fez", ele: "fez", ela: "fez", a_gente: "fez", nos: "fizemos", voces: "fizeram", eles: "fizeram", elas: "fizeram" },
           imperfeito: { eu: "fazia", voce: "fazia", ele: "fazia", ela: "fazia", a_gente: "fazia", nos: "fazíamos", voces: "faziam", eles: "faziam", elas: "faziam" } },
  poder: { perfeito: { eu: "pude", voce: "pôde", ele: "pôde", ela: "pôde", a_gente: "pôde", nos: "pudemos", voces: "puderam", eles: "puderam", elas: "puderam" },
           imperfeito: { eu: "podia", voce: "podia", ele: "podia", ela: "podia", a_gente: "podia", nos: "podíamos", voces: "podiam", eles: "podiam", elas: "podiam" } },
  dizer: { perfeito: { eu: "disse", voce: "disse", ele: "disse", ela: "disse", a_gente: "disse", nos: "dissemos", voces: "disseram", eles: "disseram", elas: "disseram" },
           imperfeito: { eu: "dizia", voce: "dizia", ele: "dizia", ela: "dizia", a_gente: "dizia", nos: "dizíamos", voces: "diziam", eles: "diziam", elas: "diziam" } },
  ver: { perfeito: { eu: "vi", voce: "viu", ele: "viu", ela: "viu", a_gente: "viu", nos: "vimos", voces: "viram", eles: "viram", elas: "viram" },
        imperfeito: { eu: "via", voce: "via", ele: "via", ela: "via", a_gente: "via", nos: "víamos", voces: "viam", eles: "viam", elas: "viam" } },
  dar: { perfeito: { eu: "dei", voce: "deu", ele: "deu", ela: "deu", a_gente: "deu", nos: "demos", voces: "deram", eles: "deram", elas: "deram" },
        imperfeito: { eu: "dava", voce: "dava", ele: "dava", ela: "dava", a_gente: "dava", nos: "dávamos", voces: "davam", eles: "davam", elas: "davam" } },
  vir: { perfeito: { eu: "vim", voce: "veio", ele: "veio", ela: "veio", a_gente: "veio", nos: "viemos", voces: "vieram", eles: "vieram", elas: "vieram" },
        imperfeito: { eu: "vinha", voce: "vinha", ele: "vinha", ela: "vinha", a_gente: "vinha", nos: "vínhamos", voces: "vinham", eles: "vinham", elas: "vinham" } },
};

const PRONOUNS = [
  { key: "eu", label: "eu" },
  { key: "voce", label: "você" },
  { key: "ele", label: "ele" },
  { key: "ela", label: "ela" },
  { key: "a_gente", label: "a gente" },
  { key: "nos", label: "nós" },
  { key: "voces", label: "vocês" },
  { key: "eles", label: "eles" },
  { key: "elas", label: "elas" },
];

function conjRegular(verb: string, tense: string, pronoun: string) {
  // crude regular conjugation for -ar/-er/-ir
  const stem = verb.slice(0, -2);
  const end = verb.slice(-2);
  const map: Record<string, Record<string, Record<string, string>>> = {
    perfeito: {
      ar: { eu: "ei", voce: "ou", ele: "ou", ela: "ou", a_gente: "ou", nos: "amos", voces: "aram", eles: "aram", elas: "aram" },
      er: { eu: "i", voce: "eu", ele: "eu", ela: "eu", a_gente: "eu", nos: "emos", voces: "eram", eles: "eram", elas: "eram" },
      ir: { eu: "i", voce: "iu", ele: "iu", ela: "iu", a_gente: "iu", nos: "imos", voces: "iram", eles: "iram", elas: "iram" },
    },
    imperfeito: {
      ar: { eu: "ava", voce: "ava", ele: "ava", ela: "ava", a_gente: "ava", nos: "ávamos", voces: "avam", eles: "avam", elas: "avam" },
      er: { eu: "ia", voce: "ia", ele: "ia", ela: "ia", a_gente: "ia", nos: "íamos", voces: "iam", eles: "iam", elas: "iam" },
      ir: { eu: "ia", voce: "ia", ele: "ia", ela: "ia", a_gente: "ia", nos: "íamos", voces: "iam", eles: "iam", elas: "iam" },
    }
  };
  const g = map[tense]?.[end];
  if (!g) return verb;
  return stem + (g[pronoun] || g.voce || "");
}

function conjugate(verb: string, tense: string, pronoun: string) {
  const v = verb.toLowerCase();
  if (IRREG[v]?.[tense]?.[pronoun]) return IRREG[v][tense][pronoun];
  if (/^(.*)(ar|er|ir)$/.test(v)) return conjRegular(v, tense, pronoun);
  return verb; // fallback
}

// ——— Component ———
export default function PastTenseTrainerApp() {
  // Controls
  const [mode, setMode] = useState("contrast"); // "perfeito" | "imperfeito" | "contrast"
  const [verbs, setVerbs] = useState("ir, comer, estudar, fazer, visitar");
  const [pronoun, setPronoun] = useState("eu");
  const [level, setLevel] = useState("B1");
  const [count, setCount] = useState(8);
  const [temperature, setTemperature] = useState(0.6);
  const [mockMode, setMockMode] = useState(false);
  const [register, setRegister] = useState("informal");

  const [items, setItems] = useState<Card[]>([]); // cards from AI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const hasClaude = true; // Always available via API

  // Persist a bit
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("pt_past_prefs") || "null");
      if (saved) {
        setMode(saved.mode ?? "contrast");
        setVerbs(saved.verbs ?? "ir, comer, estudar, fazer, visitar");
        setPronoun(saved.pronoun ?? "eu");
        setLevel(saved.level ?? "B1");
        setCount(saved.count ?? 8);
        setRegister(saved.register ?? "informal");
        setTemperature(saved.temperature ?? 0.6);
      }
      const savedItems = JSON.parse(localStorage.getItem("pt_past_items") || "null");
      if (savedItems) setItems(savedItems);
    } catch {}
  }, []);

  useEffect(() => {
    const prefs = { mode, verbs, pronoun, level, count, register, temperature };
    localStorage.setItem("pt_past_prefs", JSON.stringify(prefs));
    localStorage.setItem("pt_past_items", JSON.stringify(items));
  }, [mode, verbs, pronoun, level, count, register, temperature, items]);

  // ——— Prompting ———
  const systemPrompt = useMemo(() => `You are a PT-BR teacher specializing in past tenses (pretérito perfeito & imperfeito).\nReturn ONLY strict JSON (no code fences) with:\n{\n  "cards": [\n    {\n      "prompt_en": string,             // concise scenario in English the learner replies to\n      "target_pt": string,             // ideal PT-BR answer in the requested tense(s)\n      "tense": "perfeito"|"imperfeito"|"contrast",\n      "verb": string,                  // main verb targeted\n      "pronoun": string,               // e.g. "eu", "você"\n      "explain_pt": string,            // one-sentence why this tense/wording fits\n      "explain_en": string,            // short gloss in EN\n      "hint_pt": string,               // micro hint (time expression or cue)\n      "triggers": string[]             // time markers e.g., ["ontem", "quando eu era criança"]\n    }\n  ]\n}\nGuidelines:\n- Use natural Brazilian Portuguese, CEFR-level ${level}, ${register} register.\n- If mode=contrast, produce minimal pairs (perfeito vs imperfeito) sharing the same verb/context; alternate cards.\n- Keep target sentences short (≤ 12–14 words).\n- Use everyday time cues: perfeito (de repente, ontem, semana passada, às 8h), imperfeito (sempre, costumava, todo dia, quando eu era criança).\n- Focus verbs: ${verbs}. Use the selected pronoun as the subject where natural: ${pronoun}.\n- Create exactly ${count} cards.\n`, [level, register, verbs, pronoun, count]);

  function buildUserPrompt() {
    return `Mode: ${mode}. Pronoun: ${pronoun}. Generate ${count} training cards.`;
  }

  async function generateCards() {
    setLoading(true); setError("");
    try {
      let respText = "";
      if (!mockMode && hasClaude) {
        const apiUrl = '/api/ai/past-tense-trainer';
        console.log('🌍 API URL:', apiUrl);
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system: systemPrompt,
            messages: [{ role: "user", content: buildUserPrompt() }],
            temperature,
            max_tokens: 1400
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success) {
          respText = data.response;
        } else {
          throw new Error(data.error || 'Failed to get AI response');
        }
      } else {
        // Mock:
        const verbsArr = verbs.split(",").map(s => s.trim()).filter(Boolean);
        const sample = { cards: Array.from({ length: count }).map((_, i) => {
          const v = verbsArr[i % verbsArr.length] || "estudar";
          const t = mode === "contrast" ? (i % 2 === 0 ? "perfeito" : "imperfeito") : mode;
          const target = buildSampleSentence(v, t, pronoun);
          return {
            prompt_en: t === "perfeito" ? `Say what you did with "${v}" (finished).` : `Say what you used to do with "${v}" (habit/background).`,
            target_pt: target,
            tense: t,
            verb: v,
            pronoun,
            explain_pt: t === "perfeito" ? "Ação concluída em ponto específico do passado." : "Hábito/descrição contínua no passado.",
            explain_en: t === "perfeito" ? "Completed past action." : "Ongoing/habitual past.",
            hint_pt: t === "perfeito" ? "ontem / no sábado / às 8h" : "sempre / todo dia / quando eu era criança",
            triggers: t === "perfeito" ? ["ontem", "no ano passado"] : ["sempre", "todo dia"]
          };
        }) };
        respText = JSON.stringify(sample);
        await new Promise(r => setTimeout(r, 400));
      }
      const data = parseJSONSafe(respText);
      if (data?.cards) setItems(data.cards); else throw new Error("No cards in response");
    } catch (e) {
      console.error('AI request error:', e);
      setError('Failed to connect to AI. Using mock response.');
      const verbsArr = verbs.split(",").map(s => s.trim()).filter(Boolean);
      const mockCards = Array.from({ length: count }).map((_, i) => {
        const v = verbsArr[i % verbsArr.length] || "estudar";
        const t = mode === "contrast" ? (i % 2 === 0 ? "perfeito" : "imperfeito") : mode;
        const target = buildSampleSentence(v, t, pronoun);
        return {
          prompt_en: t === "perfeito" ? `Say what you did with "${v}" (finished).` : `Say what you used to do with "${v}" (habit/background).`,
          target_pt: target,
          tense: t as "perfeito" | "imperfeito" | "contrast",
          verb: v,
          pronoun,
          explain_pt: t === "perfeito" ? "Ação concluída em ponto específico do passado." : "Hábito/descrição contínua no passado.",
          explain_en: t === "perfeito" ? "Completed past action." : "Ongoing/habitual past.",
          hint_pt: t === "perfeito" ? "ontem / no sábado / às 8h" : "sempre / todo dia / quando eu era criança",
          triggers: t === "perfeito" ? ["ontem", "no ano passado"] : ["sempre", "todo dia"]
        };
      });
      setItems(mockCards);
    } finally {
      setLoading(false);
    }
  }

  function coerceResponseToText(resp: any) {
    if (!resp) return "";
    if (typeof resp === "string") return resp;
    if (resp.output_text) return resp.output_text;
    if (resp.text) return resp.text;
    if (resp.completion) return resp.completion;
    if (resp.message?.content) {
      const c = resp.message.content;
      if (Array.isArray(c) && c[0]?.text) return c[0].text;
      if (typeof c === "string") return c;
    }
    try { return JSON.stringify(resp); } catch { return String(resp); }
  }

  function parseJSONSafe(text: string) {
    try {
      const first = text.indexOf("{");
      const last = text.lastIndexOf("}");
      const body = first >= 0 && last > first ? text.slice(first, last + 1) : text;
      return JSON.parse(body);
    } catch { return null; }
  }

  // ——— Sample generation without AI ———
  function buildSampleSentence(verb: string, tense: string, pron: string) {
    const obj: Record<string, Record<string, string>> = {
      ir: { perfeito: "ao mercado ontem", imperfeito: "ao mercado todo dia" },
      comer: { perfeito: "pizza no sábado", imperfeito: "fruta toda manhã" },
      estudar: { perfeito: "português ontem à noite", imperfeito: "português depois do trabalho" },
      fazer: { perfeito: "exercício às 7h", imperfeito: "exercício sempre de manhã" },
      visitar: { perfeito: "meus pais no domingo", imperfeito: "meus pais todo mês" },
    };
    const chunk = obj[verb]?.[tense] || (tense === "perfeito" ? "isso ontem" : "isso sempre");
    const ptPron = PRONOUNS.find(p => p.key === pron)?.label || "eu";
    const v = conjugate(verb, tense, pron);
    return `${ptPron} ${v} ${chunk}`;
  }

  // ——— Utilities ———
  function speak(text: string, lang = "pt-BR") {
    try { const u = new SpeechSynthesisUtterance(text); u.lang = lang; window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); } catch {}
  }
  function copy(text: string) { navigator.clipboard?.writeText(text); }
  function exportJSON() {
    const blob = new Blob([JSON.stringify({ cards: items }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "ptbr_past_tense_cards.json"; a.click(); URL.revokeObjectURL(url);
  }

  // ——— Conjugation Panel ———
  const [helperVerb, setHelperVerb] = useState("estudar");
  const [helperTense, setHelperTense] = useState("perfeito");

  function renderConjRow(p: any) {
    const v = conjugate(helperVerb, helperTense, p.key);
    return (
      <div key={p.key} className="flex items-center justify-between py-1 border-b last:border-b-0 text-sm">
        <span className="text-neutral-600">{p.label}</span>
        <span className="font-medium">{v}</span>
      </div>
    );
  }

  // ——— Card Component ———
  function Card({ c, idx }: { c: Card; idx: number }) {
    const [guess, setGuess] = useState("");
    const [revealed, setRevealed] = useState(false);
    const ok = isCloseMatch(guess, c.target_pt);

    return (
      <article className="bg-white rounded-2xl shadow-sm border p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500">{c.tense === "perfeito" ? "Pretérito Perfeito" : c.tense === "imperfeito" ? "Pretérito Imperfeito" : "Contrast"}</div>
            <h3 className="text-base font-semibold leading-snug">{c.prompt_en}</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] px-2 py-1 rounded-full bg-neutral-100 border">{c.verb}</span>
            <span className="text-[10px] px-2 py-1 rounded-full bg-neutral-100 border">{c.pronoun}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            className={`flex-1 rounded-xl border px-3 py-2 outline-none focus:ring-2 ${ok ? 'border-emerald-400 ring-emerald-200' : 'focus:ring-neutral-800'}`}
            placeholder="Type your Portuguese answer…"
            value={guess}
            onChange={(e)=>setGuess(e.target.value)}
          />
          <button onClick={() => setRevealed(true)} className="p-2 rounded-xl border" title="Reveal">
            <IconEye className="w-4 h-4"/>
          </button>
          <button onClick={() => speak(c.target_pt)} className="p-2 rounded-xl border" title="Play (pt-BR)">
            <IconPlay className="w-4 h-4"/>
          </button>
        </div>

        {c.hint_pt && (
          <div className="text-xs text-neutral-600"><span className="font-medium">Hint:</span> {c.hint_pt} {c.triggers?.length ? `• ${c.triggers.join(' • ')}` : ''}</div>
        )}

        <div className="text-sm">
          {revealed ? (
            <div className="rounded-xl border bg-neutral-50 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-neutral-500 text-xs">Model answer</div>
                  <div className="font-medium">{c.target_pt}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-1 rounded-lg border" title="Copy" onClick={()=>copy(c.target_pt)}><IconCopy className="w-4 h-4"/></button>
                  <button className="p-1 rounded-lg border" title="Play" onClick={()=>speak(c.target_pt)}><IconPlay className="w-4 h-4"/></button>
                </div>
              </div>
              <div className="mt-2 text-xs text-neutral-700">
                <span className="font-medium">PT:</span> {c.explain_pt}
              </div>
              <div className="text-xs text-neutral-700">
                <span className="font-medium">EN:</span> {c.explain_en}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm">
              <IconCheck className={`w-4 h-4 ${ok ? 'text-emerald-600' : 'text-neutral-300'}`}/>
              <span className={ok ? 'text-emerald-700' : 'text-neutral-500'}>{ok ? 'Looks good!' : 'Type your answer and reveal to check.'}</span>
            </div>
          )}
        </div>
      </article>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <div className="mx-auto max-w-6xl px-4 py-8">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">PT‑BR Past Tense Trainer — Perfeito × Imperfeito</h1>
            <p className="text-sm text-neutral-600 mt-1">Type your answer, then <span className="font-medium">Reveal</span>. Uses time cues to pick the right tense.</p>
          </div>
          <div className="flex items-center gap-3">
            {!hasClaude && !mockMode && (
              <span className="rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs">Connect Claude to go live</span>
            )}
            <label className="flex items-center gap-2 text-xs select-none">
              <input type="checkbox" className="scale-110" checked={mockMode} onChange={(e)=>setMockMode(e.target.checked)} />
              Mock mode
            </label>
          </div>
        </header>

        <section className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-2xl shadow-sm border p-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-neutral-600">Mode</label>
                <select className="mt-1 w-full rounded-xl border px-3 py-2" value={mode} onChange={(e)=>setMode(e.target.value)}>
                  <option value="perfeito">Perfeito (completed)</option>
                  <option value="imperfeito">Imperfeito (habit/background)</option>
                  <option value="contrast">Contrast (minimal pairs)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Pronoun</label>
                <select className="mt-1 w-full rounded-xl border px-3 py-2" value={pronoun} onChange={(e)=>setPronoun(e.target.value)}>
                  {PRONOUNS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Level (CEFR)</label>
                <select className="mt-1 w-full rounded-xl border px-3 py-2" value={level} onChange={(e)=>setLevel(e.target.value)}>
                  {["A2","B1","B2"].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Register</label>
                <select className="mt-1 w-full rounded-xl border px-3 py-2" value={register} onChange={(e)=>setRegister(e.target.value)}>
                  {["informal","neutral","formal"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <label className="block text-xs font-medium text-neutral-600 mt-4">Focus verbs (comma‑separated)</label>
            <input className="mt-1 w-full rounded-xl border px-3 py-2" value={verbs} onChange={(e)=>setVerbs(e.target.value)} placeholder="ex.: ir, comer, estudar, fazer, visitar"/>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-600">Cards: {count}</label>
                <input type="range" min={4} max={14} value={count} onChange={(e)=>setCount(parseInt(e.target.value))} className="w-full"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-600">Creativity (temp): {temperature.toFixed(2)}</label>
                <input type="range" min={0} max={1} step={0.05} value={temperature} onChange={(e)=>setTemperature(parseFloat(e.target.value))} className="w-full"/>
              </div>
            </div>
          </div>

          {/* Conjugation helper */}
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Conjugation Helper</h3>
              <div className="text-[10px] text-neutral-500">(regular + key irregulars)</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input className="rounded-xl border px-3 py-2" value={helperVerb} onChange={(e)=>setHelperVerb(e.target.value.trim().toLowerCase())} placeholder="verb (infinitive)"/>
              <select className="rounded-xl border px-3 py-2" value={helperTense} onChange={(e)=>setHelperTense(e.target.value)}>
                <option value="perfeito">Perfeito</option>
                <option value="imperfeito">Imperfeito</option>
              </select>
            </div>
            <div className="mt-3 divide-y">
              {PRONOUNS.map(renderConjRow)}
            </div>
          </div>
        </section>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button onClick={generateCards} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-neutral-900 text-white disabled:opacity-60">
            <IconWand className="w-4 h-4"/> {loading ? "Generating…" : "Generate deck"}
          </button>
          <button onClick={exportJSON} disabled={!items?.length} className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 border">
            <IconDownload className="w-4 h-4"/> Export JSON
          </button>
          <span className="text-xs text-neutral-500">Tip: Type, then Reveal. Aim for the right tense using the hints.</span>
        </div>

        {/* Cards */}
        <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items?.map((c, idx) => (
            <Card key={idx} c={c} idx={idx} />
          ))}
        </section>

        {!items?.length && (
          <div className="mt-10 text-center text-neutral-500">
            <p className="text-sm">No cards yet. Pick a mode, adjust verbs, and click <span className="font-medium">Generate deck</span>.</p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-neutral-200">ontem (perfeito)</span>
              <span className="px-2 py-1 rounded-full bg-neutral-200">todo dia (imperfeito)</span>
              <span className="px-2 py-1 rounded-full bg-neutral-200">quando eu era criança (imperfeito)</span>
            </div>
          </div>
        )}

        <footer className="mt-10 text-center text-[11px] text-neutral-500">
          Built for speaking drills in past tenses. — If your environment doesn’t expose <code>window.claude.create</code>, enable <b>Mock mode</b> above.
        </footer>
      </div>
    </div>
  );
}
